import { useDownloadCommercialEvaluationPdf } from '@src/main/modules/reports/commercial/application/hook/useDownloadCommercialEvaluation';
import { useDownloadFlightEvaluationPdf } from '@src/main/modules/reports/flight/application/hook/useDownloadFlightEvaluation';
import { useDownloadPunctualityReport } from '@src/main/modules/reports/punctuality/application/hook/useDownloadPunctualityReport';
import { getTimestamp } from '@src/main/shared/utils/dateUtils';
import { logger } from '@src/main/shared/utils/logger';
import saveAs from 'file-saver';
import { ProgressSpinner } from 'primereact/progressspinner';
import { FC, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

type PdfDownloadType = 'forms' | 'flight-evaluations' | 'commercial-evaluations';

type PdfDownloadBridgeProps = {
  type: PdfDownloadType;
};

const filePrefixByType: Record<PdfDownloadType, string> = {
  forms: 'punctuality_report',
  'flight-evaluations': 'flight_evaluation',
  'commercial-evaluations': 'commercial_evaluation',
};

const redirectPathByType: Record<PdfDownloadType, string> = {
  forms: '/reports',
  'flight-evaluations': '/reports/evaluations',
  'commercial-evaluations': '/reports/evaluations',
};

const PdfDownloadBridge: FC<PdfDownloadBridgeProps> = ({ type }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const hasStartedRef = useRef(false);
  const [hasError, setHasError] = useState(false);
  const { mutateAsync: downloadPunctualityPdf } = useDownloadPunctualityReport();
  const { mutateAsync: downloadFlightPdf } = useDownloadFlightEvaluationPdf();
  const { mutateAsync: downloadCommercialPdf } = useDownloadCommercialEvaluationPdf();

  useEffect(() => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;

    const download = async () => {
      if (!id) {
        setHasError(true);
        return;
      }

      try {
        let blob: Blob;

        if (type === 'forms') {
          blob = await downloadPunctualityPdf(id);
        } else if (type === 'flight-evaluations') {
          blob = await downloadFlightPdf(id);
        } else {
          blob = await downloadCommercialPdf(id);
        }

        const fileName = `${filePrefixByType[type]}_${id}_${getTimestamp()}.pdf`;
        saveAs(blob, fileName);
        navigate(redirectPathByType[type], { replace: true });
      } catch (error) {
        logger.error('Error downloading PDF from bridge route', error);
        setHasError(true);
      }
    };

    download();
  }, [downloadCommercialPdf, downloadFlightPdf, downloadPunctualityPdf, id, navigate, type]);

  if (hasError) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem 1rem' }}>
        <div style={{ textAlign: 'center' }}>
          <p>No se ha podido descargar el PDF.</p>
          <button
            type="button"
            onClick={() => navigate(redirectPathByType[type], { replace: true })}
          >
            Volver
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem 1rem' }}>
      <ProgressSpinner />
    </div>
  );
};

export default PdfDownloadBridge;
